import { useEffect, useId, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { Button, Divider, Icon } from '@ds/core';
import { Alert } from '@ds/feedback';
import { Checkbox, FormField, Input } from '@ds/forms';
import { MarcaDoGoogle } from '@/comuns/MarcaDoGoogle';
import { abrirSuporte } from '@/comuns/suporte';
import { VERSAO_DO_PRODUTO } from '@/data/novidades';
import { PARAM_DESTINO, destinoSeguro } from '@/router/guardas';
import { definirLembrarSessao, lembrarSessao } from '@/session/lembrarDeMim';
import {
    SENHA_DEMO, USUARIOS, entrar as autenticar, entrarComGoogle, googleDisponivel, rotuloPapel, surfacePermitida,
} from '@/session/sessao';
import { SURFACES } from '@/router/rotas';

const LINK = { color: 'var(--text-brand)', fontWeight: 600 } as const;

/**
 * Entrada da aplicação.
 *
 * A tela existe aqui, e não como marca-lugar, porque sem ela as guardas não
 * fecham o ciclo: elas mandam para `/entrar?destino=…` e alguém precisa validar
 * o destino e voltar para ele. O desenho segue a referência da Fórmula MED
 * (15/09/2026); a moldura escura e o fundo em movimento são do `AuthShell`.
 */
export function Entrar() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [mostrarSenha, setMostrarSenha] = useState(false);
    const [lembrar, setLembrar] = useState(() => lembrarSessao());
    const [erro, setErro] = useState<string | null>(null);
    const [entrando, setEntrando] = useState(false);
    const [comGoogle, setComGoogle] = useState(false);
    const [indoAoGoogle, setIndoAoGoogle] = useState(false);
    const idEmail = useId();
    const idSenha = useId();

    /* O botão só existe com o Google ligado no servidor: sem isso o clique levaria
       a uma página de erro do servidor de login. */
    useEffect(() => {
        let vivo = true;
        void googleDisponivel().then((sim) => { if (vivo) setComGoogle(sim); });
        return () => { vivo = false; };
    }, []);

    const entrar = async () => {
        setEntrando(true);
        /* Antes do login: a sessão nasce já no armazenamento escolhido. */
        definirLembrarSessao(lembrar);
        const r = await autenticar(email, senha);
        setEntrando(false);
        if (!r.ok) {
            setErro(r.erro);
            return;
        }
        /* O destino vem da URL, então passa pelo filtro antes de virar
           navegação — e a alternativa é a surface que o perfil permite, não uma
           constante: o super admin não tem /app para onde voltar. */
        navigate(destinoSeguro(params.get(PARAM_DESTINO), SURFACES[surfacePermitida()].basePath), { replace: true });
    };

    /* A volta do Google cai em `/criar-conta`: quem já é da equipe segue direto para
       dentro dali, e quem não tem clínica continua o cadastro. O destino pedido vai junto. */
    const irAoGoogle = async () => {
        setErro(null);
        setIndoAoGoogle(true);
        definirLembrarSessao(lembrar);
        const destino = params.get(PARAM_DESTINO);
        const volta = `${window.location.origin}/criar-conta${destino ? `?${PARAM_DESTINO}=${encodeURIComponent(destino)}` : ''}`;
        const r = await entrarComGoogle(volta);
        if (!r.ok) {
            setIndoAoGoogle(false);
            setErro(r.erro ?? 'Não foi possível abrir o Google.');
        }
    };

    return (
        <form
            onSubmit={(e) => { e.preventDefault(); void entrar(); }}
            style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
            <div style={{ textAlign: 'center' }}>
                <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 26, lineHeight: '32px', fontWeight: 600, letterSpacing: '-.015em', color: 'var(--text-primary)' }}>
                    Entrar na plataforma
                </h1>
                <p style={{ margin: '6px 0 0', fontSize: 14.5, color: 'var(--text-secondary)' }}>Acesse sua conta para continuar.</p>
                {/* Senha e contas de demonstração só no ambiente de desenvolvimento.
                    No ar, esta tela entregava a qualquer visitante a conta de
                    administrador da plataforma — que vê todas as clínicas. */}
                {import.meta.env.DEV && (
                    <p style={{ margin: '6px 0 0', fontSize: 13.5, color: 'var(--text-secondary)' }}>
                        Protótipo: a senha de todas as contas de demonstração é <strong>{SENHA_DEMO}</strong>.
                    </p>
                )}
            </div>

            {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

            <FormField label="E-mail" htmlFor={idEmail} required>
                <Input
                    id={idEmail} type="email" name="email" autoComplete="username" value={email} onChange={setEmail}
                    placeholder="seu@email.com" invalid={!!erro} iconLeft={<Icon name="mail" size={16} />}
                />
            </FormField>

            <FormField label="Senha" htmlFor={idSenha} required>
                <Input
                    id={idSenha} type={mostrarSenha ? 'text' : 'password'} name="senha" autoComplete="current-password"
                    value={senha} onChange={setSenha} placeholder="Sua senha" invalid={!!erro} iconLeft={<Icon name="lock" size={16} />}
                    suffix={(
                        <button
                            type="button" onClick={() => setMostrarSenha((m) => !m)}
                            aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'} aria-pressed={mostrarSenha}
                            style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'inherit', display: 'flex', padding: 4, borderRadius: 'var(--radius-xs)' }}
                        >
                            <Icon name={mostrarSenha ? 'eye-off' : 'eye'} size={17} />
                        </button>
                    )}
                />
            </FormField>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', fontSize: 13.5 }}>
                <Checkbox checked={lembrar} onChange={setLembrar} label="Lembrar de mim" />
                <Link to="/recuperar-senha" style={LINK}>Esqueci minha senha</Link>
            </div>

            <Button type="submit" size="lg" fullWidth loading={entrando} iconRight={<Icon name="arrow-right" size={18} />}>Entrar</Button>

            {comGoogle && (
                <>
                    <Divider label="ou" spacing={0} />
                    <Button type="button" variant="secondary" fullWidth loading={indoAoGoogle} iconLeft={<MarcaDoGoogle />} onClick={() => { void irAoGoogle(); }}>
                        Entrar com Google
                    </Button>
                </>
            )}

            <Divider label="Precisa de ajuda?" spacing={0} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, fontSize: 13.5 }}>
                <button
                    type="button" onClick={() => abrirSuporte(VERSAO_DO_PRODUTO)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: 0, background: 'transparent', cursor: 'pointer', padding: '4px 6px', fontFamily: 'var(--font-ui)', fontSize: 15, ...LINK }}
                >
                    <Icon name="headphones" size={18} />
                    Falar com o suporte
                </button>
                <span style={{ color: 'var(--text-secondary)' }}>
                    Recebeu um convite? <Link to="/criar-conta" style={LINK}>Criar conta</Link>
                </span>
            </div>

            {import.meta.env.DEV && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
                <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>Contas de demonstração</span>
                {USUARIOS.map((u) => (
                    <button
                        key={u.email} type="button"
                        onClick={() => { setEmail(u.email); setSenha(SENHA_DEMO); setErro(null); }}
                        /* Envolve. Em 320 px — o menor telefone que ainda se usa — nome,
                           papel e e-mail não cabem na mesma linha, e sem `wrap` o e-mail
                           passava da janela e fazia a TELA DE ENTRAR rolar de lado. É a
                           tela mais visitada do produto; era o pior lugar para isso. */
                        style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', border: 0, background: 'transparent', padding: '4px 0', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-ui)', fontSize: 12.5, color: 'var(--text-secondary)' }}
                    >
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{u.nome}</span>
                        <span>{rotuloPapel(u.papel)}</span>
                        <span style={{ marginLeft: 'auto', minWidth: 0, color: 'var(--text-tertiary)' }}>{u.email}</span>
                    </button>
                ))}
            </div>
            )}
        </form>
    );
}
